import {
    getPredefinedGroupLabel,
    nestLeavesUnderSections,
    orderedPredefinedLeafTypes,
} from "@common/constants/domains/accounting/accountGroups";
import { AccountType } from "@common/constants/domains/accounting/accounts";
import { shouldPruneEmptyGroup } from "@common/helpers/domains/accounting/chartRowShaping";
import { NestedEditorRow } from "./nestedRowTypes";

// Builders hardcode type depth 0; wrapping shifts balance-sheet types down.
const reindexDepth = (
    rows: NestedEditorRow[],
    depth: number,
): NestedEditorRow[] =>
    rows.map((row) => ({
        ...row,
        depth,
        children: row.children
            ? reindexDepth(row.children, depth + 1)
            : row.children,
    }));

const countKind = (
    rows: NestedEditorRow[],
    kind: NestedEditorRow["kind"],
): number => rows.filter((row) => row.kind === kind).length;

const pruneEmptyRows = (rows: NestedEditorRow[]): NestedEditorRow[] => {
    const kept: NestedEditorRow[] = [];
    for (const row of rows) {
        if (row.kind === "account") {
            kept.push(row);
            continue;
        }
        const prunedChildren = row.children ? pruneEmptyRows(row.children) : [];
        if (
            row.kind === "group" &&
            shouldPruneEmptyGroup({
                hasActiveFilter: true,
                childGroupRowCount: countKind(prunedChildren, "group"),
                childAccountRowCount: countKind(prunedChildren, "account"),
            })
        ) {
            continue;
        }
        kept.push({ ...row, children: prunedChildren });
    }
    return kept;
};

export const pruneFilteredNestedTypeRows = (
    typeRows: NestedEditorRow[],
): NestedEditorRow[] =>
    pruneEmptyRows(typeRows).filter(
        (row) => row.kind !== "type" || (row.children?.length ?? 0) > 0,
    );

export const wrapNestedTypeRowsInSections = (
    typeRows: NestedEditorRow[],
): NestedEditorRow[] => {
    const typeRowByType = new Map<AccountType, NestedEditorRow>();
    for (const row of typeRows) {
        if (row.accountType) {
            typeRowByType.set(row.accountType, row);
        }
    }
    const wrapped = nestLeavesUnderSections<NestedEditorRow>(
        orderedPredefinedLeafTypes,
        {
            createSectionNode: (section, pathKey) => ({
                id: `section:${pathKey}`,
                kind: "section",
                name: getPredefinedGroupLabel(section),
                depth: 0,
                children: [],
                accounts: [],
                groups: [],
            }),
            getSectionChildren: (sectionRow) => sectionRow.children ?? [],
            createLeafNode: (leafType) => typeRowByType.get(leafType) ?? null,
        },
    );
    return reindexDepth(wrapped, 0);
};
